import { useState, useEffect, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { usePinnedQuotes } from "./MyPinnedQuotes";

const apiScopes = [
  "api://2350508b-6d67-408a-9c55-c5040357d90d/b351e950-d526-4ee4-b95b-03f6ec89cd3d/access_as_user",
];

export default function QuoteSelector({ onSelect, value = null, onQuotesLoaded, pendingQuoteMode = false }) {
  const { instance, accounts } = useMsal();
  const { pinQuote, unpinQuote, isQuotePinned } = usePinnedQuotes();

  const [activeQuotes, setActiveQuotes] = useState([]);  // Quotes with active jobs only
  const [allQuotes, setAllQuotes] = useState([]);        // All quotes (for searching)
  const [pendingQuotes, setPendingQuotes] = useState([]); // Quotes without orders (pending)
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(value);
  const [error, setError] = useState(null);

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Sync internal state when parent value changes
  // If only quoteNumber is provided (e.g., from Projects navigation), look up full quote
  useEffect(() => {
    if (value && value.quoteNumber && !value.user_Text4 && allQuotes.length > 0) {
      // Look up full quote from allQuotes by quoteNumber
      const fullQuote = allQuotes.find(q =>
        q.quoteNumber?.toString() === value.quoteNumber?.toString()
      );
      if (fullQuote) {
        setSelectedQuote(fullQuote);
        onSelect(fullQuote); // Update parent with full quote data
        return;
      }
    }
    setSelectedQuote(value);
  }, [value, allQuotes]);

  // Load both active quotes (default) and all quotes (for searching)
  useEffect(() => {
    async function loadQuotes() {
      if (accounts.length === 0) return;

      setLoading(true);
      setError(null);

      try {
        let tokenResponse;
        try {
          tokenResponse = await instance.acquireTokenSilent({
            scopes: apiScopes,
            account: accounts[0],
          });
        } catch (tokenError) {
          // If silent token acquisition fails due to expired refresh token,
          // redirect user to re-authenticate
          if (tokenError instanceof InteractionRequiredAuthError) {
            console.warn("Token expired, redirecting to login...");
            setError("Session expired. Redirecting to login...");
            // Clear any stale tokens and redirect to login
            await instance.loginRedirect({
              scopes: apiScopes,
              account: accounts[0],
            });
            return;
          }
          throw tokenError;
        }

        const accessToken = tokenResponse.accessToken;

        // Fetch active quotes, all quotes, and pending quotes in parallel
        const [activeRes, allRes, pendingRes] = await Promise.all([
          fetch("/api/quotes?activeOnly=true", {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          fetch("/api/quotes", {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
          fetch("/api/quotes?pendingOnly=true", {
            headers: { Authorization: `Bearer ${accessToken}` },
          }),
        ]);

        const activeData = await activeRes.json();
        const allData = await allRes.json();
        const pendingData = await pendingRes.json();

        if (!activeRes.ok) throw new Error(activeData.error || "Failed to load active quotes");
        if (!allRes.ok) throw new Error(allData.error || "Failed to load quotes");
        if (!pendingRes.ok) throw new Error(pendingData.error || "Failed to load pending quotes");

        const active = activeData.quotes || [];
        const all = allData.quotes || [];
        const pending = pendingData.quotes || [];

        setActiveQuotes(active);
        setAllQuotes(all);
        setPendingQuotes(pending);
        setFilteredQuotes(active); // Default to showing active quotes
        onQuotesLoaded?.(all);

        console.log(`📊 Loaded ${active.length} active quotes, ${all.length} total, ${pending.length} pending (no orders)`);
      } catch (err) {
        console.error("Quote load error:", err);
        setError("Unable to load quotes.");
      } finally {
        setLoading(false);
      }
    }

    loadQuotes();
  }, [accounts]);

  // Local filter
  // - Pending quote mode: show pending quotes only (quotes without orders)
  // - No search: show active quotes only
  // - With search: search across ALL quotes (or pending quotes in pending mode)
  useEffect(() => {
    // In pending quote mode, show/filter pending quotes
    if (pendingQuoteMode) {
      if (!search) {
        setFilteredQuotes(pendingQuotes);
        return;
      }
      // Search within pending quotes
      const term = search.toLowerCase();
      setFilteredQuotes(
        pendingQuotes.filter((q) => {
          return (
            (q.quoteNumber || "").toString().toLowerCase().includes(term) ||
            (q.user_Text4 || "").toLowerCase().includes(term) ||
            (q.customerName || "").toLowerCase().includes(term) ||
            (q.salesID || "").toLowerCase().includes(term)
          );
        })
      );
      return;
    }

    // Normal mode
    if (!search) {
      // No search term - show active quotes only
      setFilteredQuotes(activeQuotes);
      return;
    }

    // Search term entered - search across ALL quotes
    const term = search.toLowerCase();

    setFilteredQuotes(
      allQuotes.filter((q) => {
        return (
          (q.quoteNumber || "").toString().toLowerCase().includes(term) ||
          (q.user_Text4 || "").toLowerCase().includes(term) ||
          (q.customerName || "").toLowerCase().includes(term) ||
          (q.salesID || "").toLowerCase().includes(term)
        );
      })
    );
  }, [search, allQuotes, activeQuotes, pendingQuotes, pendingQuoteMode]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function selectQuote(q) {
    setSelectedQuote(q);
    setSearch(""); // Clear search when selecting
    setShowDropdown(false);
    onSelect(q);
  }

  function handleInputFocus() {
    // Show dropdown when focusing on input (if there's search text or no quote selected)
    if (search || !selectedQuote) {
      setShowDropdown(true);
    }
  }

  function handleInputChange(e) {
    setSearch(e.target.value);
    setShowDropdown(true); // Always show dropdown when typing
  }

  function clearSearch() {
    setSearch("");
    setShowDropdown(false);
    inputRef.current?.focus();
  }

  function clearSelection() {
    setSelectedQuote(null);
    setSearch("");
    setShowDropdown(true); // Show full list after clearing
    onSelect(null);
    inputRef.current?.focus();
  }

  async function handleTogglePin(e, quoteNumber) {
    e.stopPropagation();
    if (isQuotePinned(quoteNumber)) {
      await unpinQuote(quoteNumber);
    } else {
      await pinQuote(quoteNumber);
    }
  }

  return (
    <div ref={containerRef} className="flex flex-col w-full text-gray-900 h-[200px] relative">

      {/* SEARCH BAR - always visible and editable */}
      <div className="flex-none relative mb-2">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={selectedQuote ? `Search to change from #${selectedQuote.quoteNumber}...` : "Search all quotes by #, customer, or project..."}
          className="
            w-full px-3 py-2 pr-8 rounded-lg
            bg-white border border-gray-200
            text-sm placeholder-gray-400 text-gray-900
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500
          "
        />
        {/* Clear search text button - only show when there's search text */}
        {search && (
          <button
            onClick={clearSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600 transition-colors"
            title="Clear search"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {loading && (
        <div className="text-xs text-gray-500 italic flex-none">
          Loading quotes…
        </div>
      )}

      {error && <div className="text-xs text-red-400 flex-none">{error}</div>}

      {/* DROPDOWN RESULTS - shown when typing or focusing with search text */}
      {showDropdown && !loading && (search || !selectedQuote) && (
        <div
          className={`
            absolute top-[42px] left-0 right-0 z-30
            max-h-[280px] overflow-y-auto
            bg-white rounded-lg
            shadow-lg border
            ${pendingQuoteMode ? 'border-amber-300' : 'border-purple-300'}
          `}
        >
          {/* Header showing what list is being displayed */}
          <div className={`px-3 py-1.5 border-b border-gray-100 sticky top-0 ${pendingQuoteMode ? 'bg-amber-50' : 'bg-slate-200'}`}>
            <span className={`text-[10px] uppercase tracking-wide ${pendingQuoteMode ? 'text-amber-600' : 'text-gray-500'}`}>
              {pendingQuoteMode
                ? (search ? `Searching ${pendingQuotes.length} pending quotes` : `Pending Quotes - No Orders (${pendingQuotes.length})`)
                : (search ? `Searching all ${allQuotes.length} quotes` : `Active Quotes (${activeQuotes.length})`)}
            </span>
          </div>

          <div className="divide-y divide-gray-100">
          {filteredQuotes.length === 0 ? (
            <div className="p-3 text-xs text-gray-500">
              {pendingQuoteMode
                ? (search ? "No matches found." : "No pending quotes without orders.")
                : (search ? "No matches found." : "No active quotes.")}
            </div>
          ) : (
            filteredQuotes.slice(0, 50).map((q) => {
              const isPinned = isQuotePinned(q.quoteNumber);
              return (
                <div
                  key={q.uniqueID}
                  className={`
                    flex items-center justify-between px-3 py-2 text-sm
                    transition cursor-pointer
                    ${pendingQuoteMode
                      ? 'hover:bg-amber-50'
                      : 'hover:bg-purple-50'}
                    ${selectedQuote?.uniqueID === q.uniqueID
                      ? (pendingQuoteMode
                          ? 'bg-amber-50 border-l-2 border-amber-500'
                          : 'bg-purple-50 border-l-2 border-purple-500')
                      : ''}
                  `}
                  onClick={() => selectQuote(q)}
                >
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium ${pendingQuoteMode ? 'text-amber-700' : 'text-gray-900'}`}>
                      Quote #{q.quoteNumber}
                      {pendingQuoteMode && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded">
                          PENDING
                        </span>
                      )}
                    </div>
                    <div className={`text-xs truncate ${pendingQuoteMode ? 'text-amber-600' : 'text-gray-500'}`}>
                      {(q.user_Text4 || "No project name")} — {q.customerName || "N/A"}
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleTogglePin(e, q.quoteNumber)}
                    className={`ml-2 p-1 rounded transition-colors flex-shrink-0 ${
                      isPinned
                        ? 'text-amber-500 hover:text-amber-600'
                        : 'text-gray-500 hover:text-amber-500'
                    }`}
                    title={isPinned ? "Unpin from My Jobs" : "Pin to My Jobs"}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="17" x2="12" y2="22" />
                      <path d="M5 17h14v-1.76a2 2 0 00-1.11-1.79l-1.78-.9A2 2 0 0115 10.76V6h1a2 2 0 000-4H8a2 2 0 000 4h1v4.76a2 2 0 01-1.11 1.79l-1.78.9A2 2 0 005 15.24V17z" />
                    </svg>
                  </button>
                </div>
              );
            })
          )}
          </div>
        </div>
      )}

      {/* SELECTED SUMMARY - shown below search when a quote is selected */}
      {selectedQuote && (() => {
        const isPinned = isQuotePinned(selectedQuote.quoteNumber);
        return (
        <div className={`flex-1 overflow-y-auto bg-slate-200 rounded-lg p-3 text-sm ${pendingQuoteMode ? 'border border-amber-200' : 'border border-purple-200'}`}>
          {/* Header with quote number, pin, and clear button */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${pendingQuoteMode ? 'text-amber-700' : 'text-purple-700'}`}>
                Quote #{selectedQuote.quoteNumber}
              </span>
              {pendingQuoteMode && (
                <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-600 rounded">
                  PENDING
                </span>
              )}
              {/* Pin/Unpin button */}
              <button
                onClick={(e) => handleTogglePin(e, selectedQuote.quoteNumber)}
                className={`p-1 rounded transition-colors ${
                  isPinned
                    ? 'text-amber-500 hover:text-amber-600'
                    : 'text-gray-500 hover:text-amber-500'
                }`}
                title={isPinned ? "Unpin from My Jobs" : "Pin to My Jobs"}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="17" x2="12" y2="22" />
                  <path d="M5 17h14v-1.76a2 2 0 00-1.11-1.79l-1.78-.9A2 2 0 0115 10.76V6h1a2 2 0 000-4H8a2 2 0 000 4h1v4.76a2 2 0 01-1.11 1.79l-1.78.9A2 2 0 005 15.24V17z" />
                </svg>
              </button>
              {/* Clear selection button */}
              <button
                onClick={clearSelection}
                className="text-gray-500 hover:text-red-500 transition-colors"
                title="Clear selection"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="text-xs text-gray-500">
              {isPinned ? "Pinned" : "Type above to search"}
            </div>
          </div>

          <div>
            <span className="text-gray-500">Project:</span>{" "}
            <span className="text-gray-900">{selectedQuote.user_Text4 || "N/A"}</span>
          </div>
          <div>
            <span className="text-gray-500">Customer:</span>{" "}
            <span className="text-gray-900">{selectedQuote.customerName || "N/A"}</span>
          </div>
          <div>
            <span className="text-gray-500">Sales Rep:</span>{" "}
            <span className="text-gray-900">{selectedQuote.salesID || "N/A"}</span>
          </div>
          <div>
            <span className="text-gray-500">Ship Date:</span>{" "}
            <span className="text-gray-900">{selectedQuote.user_Date1
              ? selectedQuote.user_Date1.split("T")[0]
              : "N/A"}</span>
          </div>
        </div>
        );
      })()}

      {/* NO SELECTION - show full list when no quote selected and dropdown not showing */}
      {!selectedQuote && !showDropdown && !loading && (
        <div
          className="
            flex-1 overflow-y-auto
            bg-slate-200 border border-gray-200 rounded-lg
            divide-y divide-gray-100
          "
        >
          {filteredQuotes.length === 0 ? (
            <div className="p-3 text-xs text-gray-500">No matches found.</div>
          ) : (
            filteredQuotes.map((q) => (
              <button
                key={q.uniqueID}
                onClick={() => selectQuote(q)}
                className="
                  w-full text-left px-3 py-2 text-sm
                  hover:bg-slate-100 transition
                "
              >
                <div className="font-medium text-gray-900">
                  Quote #{q.quoteNumber}
                </div>
                <div className="text-xs text-gray-500">
                  {(q.user_Text4 || "No project name")} — {q.customerName || "N/A"}
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
